import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import {
  ApiSimpleSubscription,
  ApiSubscriptionMerchant,
  ApiSubscriptionStatus,
} from "@incendium/api";
import SubscriptionPaymentCard from "features/subscription/components/SubscriptionPaymentCard";
import { getSubscriptionPlanStyle } from "features/subscription/utils/planStyles";
import Shopify_logo from "Assets/Images/ThirdParty/shopify_logo_black.png";
import Stripe_logo from "Assets/Images/ThirdParty/stripe-small.png";
import Image from "mui-image";
import StyledDrawer, {
  StyledDrawerContainer,
} from "Components/UI/StyledDrawer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { centsToDollars } from "Helpers/numbers";

interface ISubscriptionCardProps {
  subscription: ApiSimpleSubscription;
  onEdit?: (s: ApiSimpleSubscription) => void;
  onDelete?: (s: ApiSimpleSubscription) => void;
  onActivate?: (b: boolean, s: ApiSimpleSubscription) => void;
  onMakePublic?: (b: boolean, s: ApiSimpleSubscription) => void;
  onSelect?: (s: ApiSimpleSubscription, isAnnual?: boolean) => void;
  onShowProjects?: (s: ApiSimpleSubscription) => void;
  dense?: boolean;
  isCurrent?: boolean;
  /** When isCurrent, which billing period the project is on */
  currentIsAnnual?: boolean;
  background?: string;
  borderColor?: string;
  sliderCurrentSubscriptionId?: string;
}

function SubscriptionCard({
  subscription,
  onEdit,
  onDelete,
  onActivate,
  onMakePublic,
  onSelect,
  onShowProjects,
  isCurrent,
  currentIsAnnual,
  dense,
  background,
  borderColor,
  sliderCurrentSubscriptionId,
}: ISubscriptionCardProps) {
  const [termsAccepeted, setTermsAccepeted] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  const planStyle = useMemo(
    () =>
      getSubscriptionPlanStyle(subscription.product?.tier ?? subscription.tier),
    [subscription.product?.tier, subscription.tier],
  );

  const cardBackground = background ?? planStyle.bg;
  const cardBorderColor = borderColor ?? planStyle.border;

  useEffect(() => {
    if (sliderCurrentSubscriptionId === subscription.id) {
      return
    }
    setTermsAccepeted(false);
  }, [sliderCurrentSubscriptionId, subscription.id]);

  const parsedTerms = useMemo(() => {
    const textArea = document.createElement("textarea");
    textArea.innerHTML = subscription.contract?.body || "";
    return textArea.value;
  }, [subscription.contract?.body]);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  const showActions = !!(
    onEdit ||
    onDelete ||
    onActivate ||
    onMakePublic ||
    onSelect ||
    onShowProjects ||
    isCurrent
  );

  const isShopify =
    subscription.merchant ===
    ApiSubscriptionMerchant.SUBSCRIPTION_MERCHANT_SHOPIFY;

  const activateDisabledReason = useMemo(() => {
    const reasons: string[] = [];
    if (subscription._public) {
      reasons.push("public subscriptions cannot be activated from here");
    }
    if (!subscription.contractId) {
      reasons.push("a contract is required");
    }
    if (!subscription.productId) {
      reasons.push("a product is required");
    }
    if (reasons.length === 0) {
      return null;
    }
    return `Activation is disabled: ${reasons.join("; ")}.`;
  }, [subscription._public, subscription.contractId, subscription.productId]);

  const activateTooltip = activateDisabledReason
    ? activateDisabledReason
    : `Activating this subscription will create a subscription product in ${isShopify ? "Shopify" : "Stripe"
    }.`;

  return (
    <Box
      sx={{
        background: cardBackground,
        borderRadius: 2,
        position: "relative",
        overflow: "hidden",
        "&:after": {
          content: "''",
          position: "absolute",
          top: 0,
          height: 6,
          width: "100%",
          background: cardBorderColor,
        },
      }}
    >
      <SubscriptionPaymentCard
        dense={dense}
        name={subscription.product?.name || ""}
        description={subscription.product?.description || ""}
        planDescription={subscription.description || ""}
        price={subscription.price || 0}
        annualPrice={subscription.annualPrice || 0}
        merchant={
          subscription.merchant ||
          ApiSubscriptionMerchant.SUBSCRIPTION_MERCHANT_STRIPE
        }
        hidePayment
        action={
          showActions ? (
            <Stack spacing={1.5} pt={0.5}>
              <Divider />
              <Box
                sx={{ height: 28, display: "flex", justifyContent: "center" }}
              >
                <Image
                  height={28}
                  src={isShopify ? Shopify_logo : Stripe_logo}
                  duration={50}
                  fit="contain"
                  objectPosition="center"
                  sx={{ objectPosition: "center" }}
                />
              </Box>

              {onShowProjects && (
                <>
                  <Divider />
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => onShowProjects(subscription)}
                  >
                    {subscription.subscribed?.length} projects subscribed to
                    this plan, click to show
                  </Button>
                </>
              )}

              {(onEdit || onDelete || onActivate) && (
                <>
                  <Divider />
                  <Stack direction="row" pl={1} justifyContent="space-between">
                    <Stack direction="row">
                      {onEdit && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => onEdit(subscription)}
                        >
                          <Edit />
                        </IconButton>
                      )}
                      {onDelete && (
                        <IconButton
                          disabled={
                            subscription.status ===
                            ApiSubscriptionStatus.SUBSCRIPTION_STATUS_ACTIVE
                          }
                          size="small"
                          color="secondary"
                          onClick={() => onDelete(subscription)}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Stack>
                    {onActivate && (
                      <Tooltip arrow title={activateTooltip}>
                        <span>
                          <FormControlLabel
                            control={
                              <Switch
                                disabled={!!activateDisabledReason}
                                checked={
                                  subscription.status ===
                                  ApiSubscriptionStatus.SUBSCRIPTION_STATUS_ACTIVE
                                }
                                onChange={(e, c) => {
                                  onActivate(c, subscription);
                                }}
                              />
                            }
                            label="Activate"
                          />
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                </>
              )}

              {onMakePublic && (
                <Stack alignItems="flex-end" spacing={1}>
                  <Divider flexItem />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={subscription._public}
                        onChange={(e, c) => {
                          onMakePublic(c, subscription);
                        }}
                      />
                    }
                    label="Make Public"
                  />
                </Stack>
              )}

              {onSelect && !isCurrent && (
                <>
                  <Divider />
                  <Stack alignItems="center" spacing={0.5}>
                    <FormControlLabel
                      componentsProps={{
                        typography: {
                          variant: "body2",
                        },
                      }}
                      control={
                        <Switch
                          checked={termsAccepeted}
                          onChange={() => {
                            setTermsAccepeted(true);
                          }}
                        />
                      }
                      label="Accept terms"
                      sx={{ marginRight: 0 }}
                    />
                    <Button
                      onClick={() => setOpen(true)}
                      variant="text"
                      size="small"
                    >
                      View terms and conditions
                    </Button>
                  </Stack>
                  {(subscription.price || 0) > 0 &&
                    <Button
                      disabled={
                        !termsAccepeted || (isCurrent && !currentIsAnnual)
                      }
                      fullWidth
                      onClick={() => onSelect(subscription)}
                    >
                      {isCurrent && !currentIsAnnual
                        ? "Current Monthly Plan"
                        : `Select Monthly Plan ( $${centsToDollars(
                          subscription.price || 0,
                        )} /mo)`}
                    </Button>
                  }
                  {(subscription.annualPrice || 0) > 0 && (
                    <Button
                      disabled={!termsAccepeted}
                      fullWidth
                      onClick={() => onSelect(subscription, true)}
                    >
                      Select Annual Plan ( $
                      {centsToDollars(subscription.annualPrice || 0)} /yr)
                    </Button>
                  )}
                </>
              )}

              {isCurrent && !onSelect && (
                <>
                  <Divider />
                  <Typography
                    variant="body2"
                    color="primary"
                    textAlign="center"
                    fontWeight={600}
                  >
                    Current {currentIsAnnual ? "Annual" : "Monthly"} Plan
                  </Typography>
                </>
              )}
            </Stack>
          ) : undefined
        }
      />
      <StyledDrawer open={open} onClose={onClose} sx={{ zIndex: 9999 }}>
        <StyledDrawerContainer width={1100}>
          <div
            dangerouslySetInnerHTML={{
              __html: parsedTerms || "",
            }}
          />
        </StyledDrawerContainer>
      </StyledDrawer>
    </Box>
  );
}

export default SubscriptionCard;
